/**
 * Column picker for `data_list.columns` (ADR-0010 S5). A chip list (selected
 * columns, removable) + an "add" dropdown sourced from the bound query's
 * columns (derived from the preview rows). Falls back to free-text add when
 * no options are known. Fixes the old comma-field that ate the comma on type.
 */
import { X } from "lucide-react";
import { useState } from "react";

export function ColumnsField({
  value,
  options,
  onChange,
}: {
  value: unknown;
  /** Available column names (from the data_list's query preview). */
  options: string[];
  onChange: (v: string[]) => void;
}) {
  const selected: string[] = Array.isArray(value) ? (value as string[]) : [];
  const [text, setText] = useState("");

  const add = (col: string) => {
    const c = col.trim();
    if (c && !selected.includes(c)) onChange([...selected, c]);
  };
  const remove = (col: string) => onChange(selected.filter((c) => c !== col));
  const remaining = options.filter((o) => !selected.includes(o));

  return (
    <div className="flex flex-col gap-2">
      {selected.length === 0 ? (
        <p className="text-xs text-muted-foreground">All columns (none selected).</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {selected.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 rounded bg-surface px-2 py-0.5 text-xs">
              {c}
              <button type="button" aria-label={`Remove ${c}`} onClick={() => remove(c)}>
                <X className="h-3 w-3" strokeWidth={1.5} />
              </button>
            </span>
          ))}
        </div>
      )}
      {remaining.length > 0 ? (
        <select
          className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          value=""
          onChange={(e) => e.target.value && add(e.target.value)}
        >
          <option value="">+ add column…</option>
          {remaining.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <div className="flex gap-1">
          <input
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
            placeholder="column name"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add(text);
                setText("");
              }
            }}
          />
          <button
            type="button"
            className="rounded border border-border px-2 text-xs text-muted-foreground"
            onClick={() => {
              add(text);
              setText("");
            }}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
